import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Heart, ArrowLeft, Mail, Smartphone, Calendar, MapPin, Tag } from 'lucide-react';

const PressKit = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white border-b border-slate-100 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3 group">
              <div className="w-10 h-10 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-xl flex items-center justify-center">
                <Heart className="w-5 h-5 text-white" />
              </div>
              <span className="text-xl font-bold text-slate-800">A1C Survival Plan</span>
            </Link>
            <Link 
              to="/" 
              className="flex items-center gap-2 text-slate-600 hover:text-teal-600 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Link>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        <div className="prose prose-slate prose-lg max-w-none">
          <h1 className="text-4xl font-bold text-slate-800 mb-8">Press Kit</h1>

          {/* Section 1: Product Introduction */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-slate-800 mb-4">1. Product Introduction</h2>
            <div className="bg-gradient-to-br from-teal-50 to-cyan-50 rounded-3xl p-8">
              <p className="text-lg text-slate-700 leading-relaxed mb-0">
                <span className="font-semibold text-teal-700">A1C Survival Plan</span> is a journal-centered 
                diabetes management app designed to help people organize daily life and understand patterns 
                that influence long-term diabetes health.
              </p>
            </div>
          </section>

          <hr className="my-10 border-slate-200" />

          {/* Section 2: Press Contact */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-slate-800 mb-4">2. Press Contact</h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              If you have questions or requests, contact:
            </p>
            <div className="bg-slate-50 rounded-2xl p-6">
              <div className="flex items-center gap-3 mb-3">
                <Mail className="w-5 h-5 text-teal-500" />
                <div>
                  <span className="font-medium text-slate-700">Email: </span>
                  <a href="mailto:support@a1csurvivalplan.com" className="text-teal-600 hover:text-teal-700">
                    support@a1csurvivalplan.com
                  </a>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Heart className="w-5 h-5 text-teal-500" />
                <div>
                  <span className="font-medium text-slate-700">App Name: </span>
                  <span className="text-slate-600">A1C Survival Plan</span>
                </div>
              </div>
            </div>
          </section>

          <hr className="my-10 border-slate-200" />

          {/* Section 3: Product Snapshot */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-slate-800 mb-4">3. Product Snapshot</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="bg-slate-50 rounded-2xl p-5 flex items-center gap-4">
                <div className="w-10 h-10 bg-teal-100 rounded-xl flex items-center justify-center">
                  <Heart className="w-5 h-5 text-teal-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Product</p>
                  <p className="font-medium text-slate-800">A1C Survival Plan</p>
                </div>
              </div>
              <div className="bg-slate-50 rounded-2xl p-5 flex items-center gap-4">
                <div className="w-10 h-10 bg-teal-100 rounded-xl flex items-center justify-center">
                  <Tag className="w-5 h-5 text-teal-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Category</p>
                  <p className="font-medium text-slate-800">Health & Wellness / Diabetes</p>
                </div>
              </div>
              <div className="bg-slate-50 rounded-2xl p-5 flex items-center gap-4">
                <div className="w-10 h-10 bg-teal-100 rounded-xl flex items-center justify-center">
                  <Smartphone className="w-5 h-5 text-teal-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Platform</p>
                  <p className="font-medium text-slate-800">iOS</p>
                </div>
              </div>
              <div className="bg-slate-50 rounded-2xl p-5 flex items-center gap-4">
                <div className="w-10 h-10 bg-teal-100 rounded-xl flex items-center justify-center">
                  <Calendar className="w-5 h-5 text-teal-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Founded</p>
                  <p className="font-medium text-slate-800">2025</p>
                </div>
              </div>
            </div>
            <div className="bg-gradient-to-r from-teal-500 to-cyan-500 rounded-2xl p-5 mt-4 text-white">
              <p className="text-sm text-white/80">Focus</p>
              <p className="font-medium text-lg">Journal-centered diabetes management</p>
            </div>
          </section>

          <hr className="my-10 border-slate-200" />

          {/* Section 4: Founder / Origin Story */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-slate-800 mb-4">4. Founder / Origin Story</h2>
            <div className="bg-gradient-to-br from-slate-50 to-teal-50/30 rounded-3xl p-8">
              <p className="text-lg text-slate-700 leading-relaxed mb-0">
                A1C Survival Plan was created after its founder struggled to find one place that brought 
                together medications, nutrition, fitness, hydration, and daily reflections into a clear, 
                shareable picture for doctor visits.
              </p>
            </div>
          </section>

          <hr className="my-10 border-slate-200" />

          {/* Section 5: App Description */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-slate-800 mb-4">5. App Description</h2>
            <div className="space-y-4 text-slate-600 leading-relaxed">
              <p>
                <span className="font-semibold text-slate-800">A1C Survival Plan</span> is a journal-centered 
                diabetes management app built to help people organize daily life and better understand the 
                patterns that shape long-term health.
              </p>
              <p>
                Designed around real-world experience, the app brings together journaling, nutrition, fitness, 
                medications, hydration, mental health, and insights into one connected system. Rather than 
                focusing on isolated metrics, A1C Survival Plan helps users tell a clear, accurate story of 
                daily life — for themselves and the people helping care for them.
              </p>
            </div>
          </section>

          <hr className="my-10 border-slate-200" />

          {/* Press Contact (bottom) */}
          <section className="mb-12">
            <h3 className="text-xl font-bold text-slate-800 mb-4">Press Contact</h3>
            <p className="text-slate-600 leading-relaxed mb-4">
              If you have questions or requests, contact:
            </p>
            <div className="bg-slate-50 rounded-2xl p-6">
              <p className="text-slate-600 mb-2">
                <span className="font-medium">Email:</span>{' '}
                <a href="mailto:support@a1csurvivalplan.com" className="text-teal-600 hover:text-teal-700">
                  support@a1csurvivalplan.com
                </a>
              </p>
              <p className="text-slate-600">
                <span className="font-medium">App Name:</span> A1C Survival Plan
              </p>
            </div>
          </section>

          {/* Quick Facts */}
          <section>
            <h3 className="text-xl font-bold text-slate-800 mb-4">Quick Facts</h3>
            <div className="bg-gradient-to-br from-teal-500 to-cyan-500 rounded-3xl p-8 text-white">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <p className="text-white/70 text-sm">Product</p>
                  <p className="font-medium text-lg">A1C Survival Plan</p>
                </div>
                <div>
                  <p className="text-white/70 text-sm">Platform</p>
                  <p className="font-medium text-lg">iOS</p>
                </div>
                <div>
                  <p className="text-white/70 text-sm">Category</p>
                  <p className="font-medium text-lg">Health & Wellness / Diabetes</p>
                </div>
                <div>
                  <p className="text-white/70 text-sm">Founded</p>
                  <p className="font-medium text-lg">2025</p>
                </div>
                <div className="sm:col-span-2">
                  <p className="text-white/70 text-sm">Location</p>
                  <p className="font-medium text-lg flex items-center gap-2">
                    <MapPin className="w-4 h-4" />
                    United States
                  </p>
                </div>
              </div>
            </div>
          </section>

        </div>
      </main>

      {/* Footer */}
      <footer className="bg-slate-50 border-t border-slate-100 mt-16">
        <div className="max-w-4xl mx-auto px-6 py-8 text-center">
          <p className="text-slate-500 text-sm">
            © {new Date().getFullYear()} A1C Survival Plan. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default PressKit;
